import React, { useEffect, useState } from 'react'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, CircularProgress, Collapse, Tooltip
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { Alert } from '@material-ui/lab'
import { getSamgovSavedRecords, deleteSamgovSavedRecord } from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'

const formatDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

const ExpandableSavedRow = ({ record, onDelete }) => {
    const [open, setOpen] = useState(false)
    return (
        <>
            <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell>{record.matchedName || '—'}</TableCell>
                <TableCell>{record.classificationType || '—'}</TableCell>
                <TableCell>{record.exclusionType || '—'}</TableCell>
                <TableCell>{record.excludingAgency || '—'}</TableCell>
                <TableCell>{record.activateDate || '—'}</TableCell>
                <TableCell>{record.terminationDate || '—'}</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>
                    <Tooltip title='Remove saved match' arrow>
                        <IconButton
                            size='small'
                            onClick={(e) => { e.stopPropagation(); onDelete(record.id) }}
                            aria-label='remove'
                        >
                            <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        size='small'
                        aria-label={open ? 'collapse' : 'expand'}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <div style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
                            <div><strong>Matched Key:</strong> {record.matchedKey || '—'}</div>
                            <div><strong>Excluding Agency:</strong> {record.excludingAgency || '—'}</div>
                            <div><strong>Activated:</strong> {record.activateDate || '—'} &nbsp;&nbsp; <strong>Terminates:</strong> {record.terminationDate || '—'}</div>
                            <div><strong>Saved:</strong> {formatDate(record.createdAt)}</div>
                            {record.notes && (
                                <div style={{ marginTop: 6 }}><strong>Notes:</strong> <span style={{ wordBreak: 'break-word' }}>{record.notes}</span></div>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const SamgovSavedRecords = ({ applicantId, refreshKey, onRecordDeleted }) => {
    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(false)

    const fetch = () => {
        setLoading(true)
        getSamgovSavedRecords({ applicationId: applicantId })()
            .then(res => setRecords(Array.isArray(res) ? res : []))
            .catch(() => Toast({ type: 'error', message: 'Failed to load saved records' }))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (applicantId) fetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicantId, refreshKey])

    const handleDelete = (recordId) => {
        deleteSamgovSavedRecord({ applicationId: applicantId, recordId })()
            .then(() => {
                Toast({ type: 'success', message: 'Record removed' })
                fetch()
                // Notify parent so results-table bookmark icons refresh.
                if (typeof onRecordDeleted === 'function') onRecordDeleted()
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to remove record' }))
    }

    if (!applicantId) return null

    return (
        <div className='cardStyle mt-3'>
            <div className='uccTitleContainer'><h5>Saved SAM.gov Matches</h5></div>
            {loading ? (
                <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={20} /></div>
            ) : records.length === 0 ? (
                <Alert severity='info'>No SAM.gov exclusion matches saved to this application yet.</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Excluding Agency</TableCell>
                                <TableCell>Activated</TableCell>
                                <TableCell>Terminates</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map(r => (
                                <ExpandableSavedRow key={r.id} record={r} onDelete={handleDelete} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    )
}

export default SamgovSavedRecords
